import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  PromptConfigOverwrite,
  GptModel,
  PARAMETER_CONFIGS,
  StringParameter,
  ParameterType,
  NumberParameter,
} from "../../../products/copy-assistant/types";
import { Text } from "../../../components/Text";
import debounce from "lodash/debounce";
import { Button } from "../../../components/tailwind";
import {
  PromptSelectField,
  PromptSliderField,
} from "../../../products/copy-assistant/PromptFormInputs/InputFields";

export type Props = {
  selectedModelName: string;
  selectedModel: GptModel;
  modelsConfig?: { [key: string]: any };
  configOverwrite: PromptConfigOverwrite;
  collectConfig: (configOverwrite: PromptConfigOverwrite) => void;
};

export const ManageEnrichPromptModelSettings: React.FC<Props> = ({
  selectedModelName,
  selectedModel,
  modelsConfig,
  configOverwrite,
  collectConfig,
}) => {
  const currentConfig = useMemo(() => {
    const overwriteConfig = configOverwrite?.[selectedModelName];
    return overwriteConfig || modelsConfig;
  }, [configOverwrite, selectedModelName, modelsConfig]);

  const supportedParameters = selectedModel?.supported_parameters || [];

  const getReasoningValue = (reasoningValue: any): string | undefined => {
    if (!reasoningValue) return undefined;
    if (typeof reasoningValue === "object" && reasoningValue.effort) {
      return reasoningValue.effort;
    }
    return undefined;
  };

  const updateParameterValue = useCallback(
    (paramName: string, value: number | string) => {
      const baseConfig = configOverwrite?.[selectedModelName] || {};
      const copy = { ...configOverwrite };

      // If value is "default", remove the parameter from config
      if (value === "default") {
        const updatedConfig = { ...baseConfig };
        delete updatedConfig[paramName];
        // If config is now empty, remove the entire model entry
        if (Object.keys(updatedConfig).length === 0) {
          delete copy[selectedModelName];
        } else {
          copy[selectedModelName] = updatedConfig;
        }
      } else {
        let transformedValue: number | string | { effort: string } = value;
        if (paramName === "reasoning" && typeof value === "string") {
          transformedValue = { effort: value };
        }

        const updatedConfig = {
          ...baseConfig,
          [paramName]: transformedValue,
        };
        copy[selectedModelName] = updatedConfig;
      }
      collectConfig(copy);
    },
    [configOverwrite, selectedModelName, collectConfig]
  );

  const resetToDefaults = useCallback(() => {
    const copy = { ...configOverwrite };
    copy[selectedModelName] = {};
    collectConfig(copy);
  }, [selectedModelName, configOverwrite, collectConfig]);

  const [JSONParseError, setJSONParseError] = useState<string | null>(null);
  const renderBody = (): React.ReactNode => {
    // Separate slider configs from select configs
    const sliderConfigs = supportedParameters
      .filter((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        return (
          config &&
          ((config as NumberParameter).type === ParameterType.FLOAT ||
            (config as NumberParameter).type === ParameterType.INT)
        );
      })
      .map((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        // Type guard: we know config exists and has min/max/step from the filter
        if (
          !config ||
          (config as NumberParameter).type === ParameterType.STRING
        ) {
          return null;
        }
        // Provide default value to prevent controlled/uncontrolled input warning
        const paramValue = currentConfig?.[paramName];
        const defaultValue = paramValue !== undefined ? paramValue : 0;
        return {
          name: paramName,
          config,
          value: defaultValue,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    const selectConfigs = supportedParameters
      .filter((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        return (
          config &&
          config.type === ParameterType.STRING &&
          (config as StringParameter).options
        );
      })
      .map((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        // Type guard: we know config exists and has options from the filter
        if (
          !config ||
          (config as StringParameter).type !== ParameterType.STRING
        ) {
          return null;
        }

        // Extract reasoning value from {"effort": "low"} format
        const overwriteConfig = configOverwrite?.[selectedModelName];
        const hasOverwrite = !!overwriteConfig?.[paramName];

        // If no overwrite, use "default" as the value
        let value: string;
        if (!hasOverwrite) {
          value = "default";
        } else if (paramName === "reasoning") {
          const extractedValue = getReasoningValue(currentConfig?.[paramName]);
          value = extractedValue || (config as StringParameter).options[0];
        } else {
          value =
            currentConfig?.[paramName] ||
            (config as StringParameter).options[0];
        }

        return {
          name: paramName,
          config,
          value,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    return (
      <>
        {selectConfigs.map(({ name, config, value }) => (
          <div className="tw-space-y-2" key={name}>
            <PromptSelectField
              variant="Enrich"
              testId={``}
              caption={config.caption || ""}
              label={config.label}
              placeholder={`Select ${config.label}`}
              dropdownOptions={[
                {
                  key: "default",
                  value: "default",
                  text: "Default",
                },
                ...(config as StringParameter).options.map((option) => ({
                  key: option,
                  value: option,
                  text: option.charAt(0).toUpperCase() + option.slice(1),
                })),
              ]}
              value={value}
              setValue={(newValue: string) =>
                updateParameterValue(name, newValue)
              }
            />
          </div>
        ))}
        {sliderConfigs.map(({ name, config, value }) => (
          <div className="tw-space-y-2" key={name}>
            <PromptSliderField
              variant="Enrich"
              caption={(config as NumberParameter).caption}
              label={(config as NumberParameter).label}
              min={(config as NumberParameter).min}
              max={(config as NumberParameter).max}
              step={(config as NumberParameter).step}
              value={value}
              setValue={(newValue: number) =>
                updateParameterValue(name, newValue)
              }
            />
          </div>
        ))}{" "}
        <div className="tw-space-y-2">
          <Button
            type="button"
            variant="secondary-alt"
            size="sm"
            icon
            onClick={resetToDefaults}
          >
            Reset to OpenRouter default
          </Button>
        </div>
        {JSONParseError && (
          <Text
            color="red"
            className="descriptive-helper-text"
            testId="prompt-form-model-settings-config-overwrite-error"
          >
            {JSONParseError}
          </Text>
        )}
      </>
    );
  };

  const updateConfigOverwrite = useCallback(
    debounce((value: string): void => {
      setJSONParseError(null);
      if (!value) {
        const copy = { ...configOverwrite };
        if (copy[selectedModelName]) {
          delete copy[selectedModelName];
        }
        collectConfig(copy);
        return;
      }
      const parsedValue = JSON.parse(value);
      const copy = { ...configOverwrite };
      copy[selectedModelName] = parsedValue;

      collectConfig(copy);
    }, 1000),
    [configOverwrite, selectedModelName, collectConfig]
  );

  useEffect(() => {
    return () => {
      updateConfigOverwrite.cancel();
    };
  }, [updateConfigOverwrite]);

  return <>{renderBody()}</>;
};
